import { openContactLinksDialog } from "@components/layouts/ContactLinksDialog";
import { ContactUrl } from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import useServerInfo from "./useServerInfo";
import useTypedSelector from "./useTypedSelector";

interface UseContactLinksProps {
  preLoginOnly?: boolean;
}

const useContactLinks = ({ preLoginOnly = false }: UseContactLinksProps = {}) => {
  const { contactBanners: preLoginContactBanners, groupedContactIcon: preLoginGroupedContactIcon } =
    useServerInfo() ?? {};
  const { contactBanners: postLoginContactBanners, groupedContactIcon: postLoginGroupedContactIcon } = useTypedSelector(
    (state) => state.auth,
  );

  const contactLinks = useMemo(() => {
    const preLoginContactLinks: ContactUrl[] =
      preLoginContactBanners?.map(
        (item) =>
          ({
            id: item?.id,
            url: item?.action?.url,
            image: item?.primaryImage?.url,
            description: item?.name,
          }) as ContactUrl,
      ) ?? [];
    const cleansedPreLinks = preLoginContactLinks.filter((link) => link.url);

    if (preLoginOnly) {
      return cleansedPreLinks as ContactUrl[];
    }

    const postLoginContactLinks: ContactUrl[] =
      postLoginContactBanners?.map(
        (item) =>
          ({
            id: item?.id,
            url: item?.action?.url,
            image: item?.primaryImage?.url,
            description: item?.name,
          }) as ContactUrl,
      ) ?? [];

    const cleansedPostLinks = postLoginContactLinks.filter((link) => link.url);

    const renderedLinks = cleansedPostLinks.length ? cleansedPostLinks : cleansedPreLinks;

    return renderedLinks as ContactUrl[];
  }, [preLoginContactBanners, postLoginContactBanners, preLoginOnly]);

  const openContact = useCallback(() => {
    if (!contactLinks?.length) return;
    if (contactLinks.length === 1) {
      window.open(contactLinks[0].url, "_blank", "noopener,noreferrer");
    } else {
      openContactLinksDialog({
        contactLinks,
      });
    }
  }, [contactLinks]);

  const groupedContactIcon = useMemo(() => {
    return postLoginGroupedContactIcon || preLoginGroupedContactIcon;
  }, [postLoginGroupedContactIcon, preLoginGroupedContactIcon]);

  return { contactLinks, groupedContactIcon, openContact };
};

export default useContactLinks;
